import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { STATUTS_LIST, STATUS_CONFIG } from '../utils/constants';
import { toast } from 'sonner';
import {
  RefreshCw, Filter, Save, Trash2, X, Eye, ChevronDown,
  Building2, Search, SlidersHorizontal, Bookmark, Columns3
} from 'lucide-react';

// Column definitions
const ALL_COLUMNS = [
  { key: 'date', label: 'Date', defaultVisible: true },
  { key: 'numero', label: 'Numéro', defaultVisible: true },
  { key: 'societe', label: 'Société', defaultVisible: true },
  { key: 'commercial', label: 'Commercial', defaultVisible: true },
  { key: 'ville', label: 'Ville', defaultVisible: true },
  { key: 'produits', label: 'Produits / Quantités', defaultVisible: true },
  { key: 'montant', label: 'Montant TTC', defaultVisible: true },
  { key: 'statut', label: 'Statut', defaultVisible: true },
  { key: 'livreur', label: 'Livreur', defaultVisible: false },
  { key: 'date_livraison', label: 'Date livraison', defaultVisible: false },
  { key: 'statut_livraison', label: 'Statut livraison', defaultVisible: false },
  { key: 'action', label: 'Action', defaultVisible: true },
];

const STORAGE_KEY_COLUMNS = 'ibn_crm_commandes_columns';

function getDefaultColumns() {
  return ALL_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: col.defaultVisible }), {});
}

function loadColumnPrefs() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_COLUMNS);
    if (stored) return { ...getDefaultColumns(), ...JSON.parse(stored) };
  } catch {}
  return getDefaultColumns();
}

const STATUT_LIV_LABELS = {
  a_planifier: { label: 'À planifier', cls: 'bg-gray-100 text-gray-600' },
  assignee: { label: 'Assignée', cls: 'bg-orange-100 text-orange-700' },
  en_cours: { label: 'En cours', cls: 'bg-blue-100 text-blue-700' },
  livree: { label: 'Livrée', cls: 'bg-emerald-100 text-emerald-700' },
  a_reassigner: { label: 'À réassigner', cls: 'bg-red-100 text-red-600' },
};

// Columns toggle dropdown
function ColumnsDropdown({ columns, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const visibleCount = Object.values(columns).filter(Boolean).length;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        data-testid="columns-toggle-btn"
      >
        <Columns3 size={14} />
        Colonnes
        <span className="text-xs bg-gray-100 px-1.5 py-0.5 rounded-full text-gray-500">{visibleCount}</span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full right-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg z-20 min-w-[220px] p-2" data-testid="columns-dropdown">
            <p className="text-[10px] font-semibold text-gray-400 uppercase px-2 py-1">Afficher / Masquer</p>
            {ALL_COLUMNS.map(col => (
              <label key={col.key} className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-gray-50 rounded-lg cursor-pointer text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={columns[col.key] ?? col.defaultVisible}
                  onChange={() => onChange(col.key)}
                  className="rounded border-gray-300 text-[hsl(222,47%,11%)] focus:ring-[hsl(222,47%,11%)]"
                  data-testid={`col-toggle-${col.key}`}
                />
                {col.label}
              </label>
            ))}
            <div className="border-t border-gray-100 mt-1 pt-1">
              <button onClick={() => { onChange('__reset'); setIsOpen(false); }}
                className="w-full text-left px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg">
                Réinitialiser par défaut
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Client Modal Component - Full client page in modal
function ClientModal({ clientId, onClose }) {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchClient = async () => {
      try {
        const res = await api.get(`/clients/${clientId}`);
        setClient(res.data);
      } catch (e) {
        toast.error('Erreur lors du chargement du client');
      } finally {
        setLoading(false);
      }
    };
    if (clientId) fetchClient();
  }, [clientId]);

  if (!clientId) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="client-modal-overlay"
    >
      <div 
        className="bg-white rounded-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={e => e.stopPropagation()}
        data-testid="client-modal"
      >
        {/* Modal Header */}
        <div className="bg-[hsl(222,47%,11%)] text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
              <Building2 size={20} />
            </div>
            <div>
              <p className="font-bold text-lg">{client?.societe || 'Chargement...'}</p>
              <p className="text-blue-200 text-sm">{client?.code_client}</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
            data-testid="close-modal-btn"
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="overflow-y-auto max-h-[calc(90vh-80px)] p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-gray-400" />
            </div>
          ) : client ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Informations générales */}
              <div className="bg-gray-50 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building2 size={16} /> Informations générales
                </h3>
                <div className="space-y-3 text-sm">
                  {[
                    { label: 'Société', value: client.societe },
                    { label: 'SIRET', value: client.siret },
                    { label: 'Gérant', value: client.gerant },
                    { label: 'Adresse', value: client.adresse },
                    { label: 'Code postal', value: client.code_postal },
                    { label: 'Ville', value: client.ville },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-gray-500">{label}</span>
                      <span className="text-gray-900 font-medium">{value || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Contacts */}
              <div className="bg-gray-50 rounded-xl p-5">
                <h3 className="font-semibold text-gray-900 mb-4">Contacts</h3>
                <div className="space-y-3 text-sm">
                  {[
                    { label: 'Email salon', value: client.email_salon },
                    { label: 'Tél salon', value: client.tel_salon },
                    { label: 'Tél portable', value: client.tel_portable },
                    { label: 'Comptable', value: client.comptable_nom },
                    { label: 'Email compta', value: client.comptable_email },
                  ].map(({ label, value }) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-gray-500">{label}</span>
                      <span className="text-gray-900 font-medium">{value || '—'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Domaine & Services */}
              {(client.domaine || client.google_drive_folder_url) && (
                <div className="bg-blue-50 rounded-xl p-5 md:col-span-2">
                  <h3 className="font-semibold text-blue-900 mb-4">Domaine & Services</h3>
                  <div className="flex flex-wrap gap-4">
                    {client.domaine && (
                      <div className="bg-white rounded-lg px-4 py-2">
                        <span className="text-xs text-gray-500">Domaine</span>
                        <p className="font-medium text-blue-700">{client.domaine}</p>
                      </div>
                    )}
                    {client.email_domaine && (
                      <div className="bg-white rounded-lg px-4 py-2">
                        <span className="text-xs text-gray-500">Email</span>
                        <p className="font-medium text-blue-700">{client.email_domaine}</p>
                      </div>
                    )}
                    {client.google_drive_folder_url && (
                      <a 
                        href={client.google_drive_folder_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-white rounded-lg px-4 py-2 hover:bg-green-50 transition-colors"
                      >
                        <span className="text-xs text-gray-500">Google Drive</span>
                        <p className="font-medium text-green-700">Ouvrir le dossier →</p>
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-12">Client introuvable</p>
          )}
        </div>

        {/* Modal Footer */}
        <div className="border-t border-gray-100 px-6 py-4 bg-gray-50 flex justify-between items-center">
          <a 
            href={`/clients/${clientId}`}
            className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
          >
            Ouvrir la fiche complète →
          </a>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm hover:bg-gray-300 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}

// Status Dropdown Component
function StatusDropdown({ currentStatus, onStatusChange, commandeId }) {
  const [isOpen, setIsOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const currentConfig = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.incomplet;

  const handleStatusChange = async (newStatus) => {
    if (newStatus === currentStatus) {
      setIsOpen(false);
      return;
    }
    
    setUpdating(true);
    try {
      await api.put(`/commandes/${commandeId}`, { statut: newStatus });
      onStatusChange(commandeId, newStatus);
      toast.success('Statut mis à jour');
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setUpdating(false);
      setIsOpen(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={updating}
        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${currentConfig.bg} ${currentConfig.text} hover:opacity-80`}
        data-testid={`status-dropdown-${commandeId}`}
      >
        {updating ? (
          <RefreshCw size={12} className="animate-spin" />
        ) : (
          <span className={`w-2 h-2 rounded-full ${currentConfig.dot}`} />
        )}
        {currentConfig.label}
        <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg z-20 min-w-[200px] max-h-[300px] overflow-y-auto">
            {STATUTS_LIST.map(status => (
              <button
                key={status.value}
                onClick={() => handleStatusChange(status.value)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left hover:bg-gray-50 transition-colors ${
                  status.value === currentStatus ? 'bg-gray-100' : ''
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${status.dot}`} />
                {status.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Saved Filters Manager
function SavedFiltersDropdown({ filters, currentFilter, onSelect, onDelete }) {
  const [isOpen, setIsOpen] = useState(false);

  if (filters.length === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
        data-testid="saved-filters-dropdown"
      >
        <Bookmark size={14} />
        Filtres sauvegardés
        <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 bg-white rounded-xl border border-gray-200 shadow-lg z-20 min-w-[220px]">
            {filters.map(filter => (
              <div 
                key={filter.id} 
                className={`flex items-center justify-between px-3 py-2 hover:bg-gray-50 ${
                  currentFilter?.id === filter.id ? 'bg-blue-50' : ''
                }`}
              >
                <button
                  onClick={() => { onSelect(filter); setIsOpen(false); }}
                  className="flex-1 text-left text-sm text-gray-700"
                >
                  {filter.name}
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(filter.id); }}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function CommandesList() {
  const navigate = useNavigate();
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  
  // Modal state
  const [selectedClientId, setSelectedClientId] = useState(null);
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    statuts: [],  // Array for multi-select
    search: '',
    commercial_id: ''
  });
  
  // Saved filters
  const [savedFilters, setSavedFilters] = useState([]);
  const [currentSavedFilter, setCurrentSavedFilter] = useState(null);
  const [filterName, setFilterName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [commerciaux, setCommerciaux] = useState([]);

  // Column visibility
  const [columns, setColumns] = useState(loadColumnPrefs);

  const toggleColumn = (key) => {
    if (key === '__reset') {
      const defaults = getDefaultColumns();
      setColumns(defaults);
      localStorage.setItem(STORAGE_KEY_COLUMNS, JSON.stringify(defaults));
      return;
    }
    setColumns(prev => {
      const next = { ...prev, [key]: !prev[key] };
      localStorage.setItem(STORAGE_KEY_COLUMNS, JSON.stringify(next));
      return next;
    });
  };

  // Load saved filters from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('ibn_crm_commandes_filters');
    if (stored) {
      try {
        setSavedFilters(JSON.parse(stored));
      } catch (e) {
        console.error('Error loading saved filters:', e);
      }
    }
    api.get('/users/commerciaux/list').then(r => setCommerciaux(r.data || [])).catch(() => {});
  }, []);

  // Fetch commandes
  const fetchCommandes = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      
      const res = await api.get(`/commandes?${params.toString()}`);
      let data = res.data.commandes || [];
      
      // Client-side filter by multiple statuts
      if (filters.statuts.length > 0) {
        data = data.filter(c => filters.statuts.includes(c.statut));
      }
      
      // Client-side filter by commercial
      if (filters.commercial_id) {
        data = data.filter(c => c.commercial_id === filters.commercial_id);
      }
      
      // Sort by date descending
      data.sort((a, b) => new Date(b.date_commande) - new Date(a.date_commande));
      
      setCommandes(data);
      setTotal(data.length);
    } catch (e) {
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCommandes();
  }, [fetchCommandes]);

  // Handle status change
  const handleStatusChange = (commandeId, newStatus) => {
    setCommandes(prev => 
      prev.map(c => c.id === commandeId ? { ...c, statut: newStatus } : c)
    );
  };

  // Save filter
  const saveFilter = () => {
    if (!filterName.trim()) {
      toast.error('Entrez un nom pour le filtre');
      return;
    }
    
    const newFilter = {
      id: Date.now().toString(),
      name: filterName.trim(),
      filters: { ...filters }
    };
    
    const updated = [...savedFilters, newFilter];
    setSavedFilters(updated);
    localStorage.setItem('ibn_crm_commandes_filters', JSON.stringify(updated));
    
    setCurrentSavedFilter(newFilter);
    setFilterName('');
    setShowSaveDialog(false);
    toast.success(`Filtre "${newFilter.name}" sauvegardé`);
  };

  // Load saved filter
  const loadFilter = (filter) => {
    setFilters(filter.filters);
    setCurrentSavedFilter(filter);
    toast.success(`Filtre "${filter.name}" chargé`);
  };

  // Delete saved filter
  const deleteFilter = (filterId) => {
    const updated = savedFilters.filter(f => f.id !== filterId);
    setSavedFilters(updated);
    localStorage.setItem('ibn_crm_commandes_filters', JSON.stringify(updated));
    
    if (currentSavedFilter?.id === filterId) {
      setCurrentSavedFilter(null);
    }
    toast.success('Filtre supprimé');
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      statuts: [],
      search: '',
      commercial_id: ''
    });
    setCurrentSavedFilter(null);
  };

  const hasActiveFilters = filters.statuts.length > 0 || filters.search || filters.commercial_id;

  // Toggle statut in multi-select
  const toggleStatut = (statut) => {
    setFilters(prev => ({
      ...prev,
      statuts: prev.statuts.includes(statut)
        ? prev.statuts.filter(s => s !== statut)
        : [...prev.statuts, statut]
    }));
  };

  return (
    <div className="min-h-screen bg-[hsl(210,20%,98%)] p-6">
      {/* Client Modal */}
      {selectedClientId && (
        <ClientModal 
          clientId={selectedClientId} 
          onClose={() => setSelectedClientId(null)} 
        />
      )}

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[hsl(222,47%,11%)]">Commandes</h1>
            <p className="text-sm text-gray-500 mt-1">{total} commande(s) trouvée(s)</p>
          </div>
          <button
            onClick={fetchCommandes}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            data-testid="refresh-btn"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
        </div>

        {/* Filters Bar */}
        <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] p-4 mb-6 shadow-sm">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher (société, numéro...)"
                value={filters.search}
                onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                data-testid="search-input"
              />
            </div>

            {/* Toggle Filters */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-[hsl(222,47%,11%)] text-white'
                  : 'border border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal size={14} />
              Filtres
              {filters.statuts.length > 0 && (
                <span className="bg-white text-[hsl(222,47%,11%)] text-xs px-1.5 py-0.5 rounded-full">
                  {filters.statuts.length}
                </span>
              )}
            </button>

            {/* Saved Filters */}
            <SavedFiltersDropdown
              filters={savedFilters}
              currentFilter={currentSavedFilter}
              onSelect={loadFilter}
              onDelete={deleteFilter}
            />

            {/* Column Visibility */}
            <ColumnsDropdown columns={columns} onChange={toggleColumn} />

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                data-testid="clear-filters-btn"
              >
                <X size={14} />
                Effacer
              </button>
            )}
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              {/* Multi-select Statuts */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-2">
                  Sélectionnez un ou plusieurs statuts
                </label>
                <div className="flex flex-wrap gap-2">
                  {STATUTS_LIST.map(s => {
                    const isSelected = filters.statuts.includes(s.value);
                    return (
                      <button
                        key={s.value}
                        onClick={() => toggleStatut(s.value)}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                          isSelected
                            ? 'bg-[hsl(222,47%,11%)] text-white border-[hsl(222,47%,11%)]'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                        data-testid={`filter-statut-${s.value}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${s.dot}`} />
                        {s.label}
                        {isSelected && <X size={12} />}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Commercial Filter */}
              {commerciaux.length > 0 && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-500 mb-2">Commercial</label>
                  <select
                    value={filters.commercial_id}
                    onChange={e => setFilters(p => ({ ...p, commercial_id: e.target.value }))}
                    className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[hsl(222,47%,11%)]"
                    data-testid="commercial-filter-commandes"
                  >
                    <option value="">Tous les commerciaux</option>
                    {commerciaux.map(c => (
                      <option key={c.id} value={c.id}>{c.prenom} {c.nom}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Save Filter Button */}
              <div className="mt-4 flex items-center gap-3">
                {!showSaveDialog ? (
                  <button
                    onClick={() => setShowSaveDialog(true)}
                    disabled={!hasActiveFilters}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="save-filter-btn"
                  >
                    <Save size={14} />
                    Enregistrer ce filtre
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      placeholder="Nom du filtre..."
                      value={filterName}
                      onChange={e => setFilterName(e.target.value)}
                      className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                      autoFocus
                      data-testid="filter-name-input"
                    />
                    <button
                      onClick={saveFilter}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700 transition-colors"
                      data-testid="confirm-save-filter-btn"
                    >
                      Sauvegarder
                    </button>
                    <button
                      onClick={() => { setShowSaveDialog(false); setFilterName(''); }}
                      className="px-3 py-2 text-gray-500 hover:text-gray-700"
                    >
                      <X size={16} />
                    </button>
                  </div>
                )}

                {currentSavedFilter && (
                  <span className="text-sm text-gray-500">
                    Filtre actif: <strong className="text-gray-700">{currentSavedFilter.name}</strong>
                  </span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Commandes Table */}
        <div className="bg-white rounded-xl border border-[hsl(214,32%,91%)] shadow-sm overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={24} className="animate-spin text-gray-400" />
            </div>
          ) : commandes.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Filter size={40} className="mx-auto mb-3 opacity-30" />
              <p>Aucune commande trouvée</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-2 text-sm text-blue-600 hover:underline"
                >
                  Effacer les filtres
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    {columns.date && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                    )}
                    {columns.numero && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Numéro</th>
                    )}
                    {columns.societe && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        <div className="flex items-center gap-1"><Building2 size={12} />Société</div>
                      </th>
                    )}
                    {columns.commercial && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Commercial</th>
                    )}
                    {columns.ville && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Ville</th>
                    )}
                    {columns.produits && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Produits</th>
                    )}
                    {columns.montant && (
                      <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Montant TTC</th>
                    )}
                    {columns.statut && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut</th>
                    )}
                    {columns.livreur && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Livreur</th>
                    )}
                    {columns.date_livraison && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date livraison</th>
                    )}
                    {columns.statut_livraison && (
                      <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Statut livraison</th>
                    )}
                    {columns.action && (
                      <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {commandes.map(commande => {
                    const lignesText = (commande.lignes || []).map(l =>
                      `${l.quantite || 1}x ${l.reference || l.designation || ''}`
                    ).join(' · ') || commande.modele || '—';
                    const slConfig = STATUT_LIV_LABELS[commande.statut_livraison] || STATUT_LIV_LABELS.a_planifier;

                    return (
                    <tr
                      key={commande.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/commandes/${commande.id}`)}
                      data-testid={`commande-row-${commande.id}`}
                    >
                      {columns.date && (
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-900">
                            {new Date(commande.date_commande).toLocaleDateString('fr-FR')}
                          </span>
                        </td>
                      )}
                      {columns.numero && (
                        <td className="px-4 py-3">
                          <span className="text-xs text-blue-600 font-mono hover:text-blue-800 hover:underline"
                            data-testid={`commande-link-${commande.id}`}>
                            {commande.numero}
                          </span>
                        </td>
                      )}
                      {columns.societe && (
                        <td className="px-4 py-3">
                          <span className="text-sm font-medium text-gray-900">{commande.client_societe || '—'}</span>
                        </td>
                      )}
                      {columns.commercial && (
                        <td className="px-4 py-3">
                          {(() => {
                            const com = commerciaux.find(c => c.id === commande.commercial_id);
                            return com ? (
                              <span className="inline-flex items-center px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-full">
                                {com.prenom} {com.nom?.[0]}.
                              </span>
                            ) : <span className="text-xs text-gray-300">—</span>;
                          })()}
                        </td>
                      )}
                      {columns.ville && (
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">{commande.client_ville || '—'}</span>
                        </td>
                      )}
                      {columns.produits && (
                        <td className="px-4 py-3 max-w-[220px]">
                          <span className="text-sm text-gray-700 truncate block" title={lignesText}>
                            {lignesText}
                          </span>
                        </td>
                      )}
                      {columns.montant && (
                        <td className="px-4 py-3 text-right">
                          <span className="text-sm font-semibold text-gray-900">
                            {commande.total_ttc != null ? `${Number(commande.total_ttc).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €` : '—'}
                          </span>
                        </td>
                      )}
                      {columns.statut && (
                        <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                          <StatusDropdown
                            currentStatus={commande.statut}
                            commandeId={commande.id}
                            onStatusChange={handleStatusChange}
                          />
                        </td>
                      )}
                      {columns.livreur && (
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-700">{commande.livreur_nom || '—'}</span>
                        </td>
                      )}
                      {columns.date_livraison && (
                        <td className="px-4 py-3">
                          <span className="text-sm text-gray-600">
                            {commande.date_livraison ? new Date(commande.date_livraison).toLocaleDateString('fr-FR') : '—'}
                          </span>
                        </td>
                      )}
                      {columns.statut_livraison && (
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-medium ${slConfig.cls}`}>
                            {slConfig.label}
                          </span>
                        </td>
                      )}
                      {columns.action && (
                        <td className="px-4 py-3 text-center" onClick={e => e.stopPropagation()}>
                          <button
                            onClick={() => setSelectedClientId(commande.client_id)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 transition-colors"
                            data-testid={`view-client-${commande.id}`}
                          >
                            <Eye size={12} />
                            Voir client
                          </button>
                        </td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
